import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, CheckCircle, Sparkles, BookOpen, Zap, Users, ArrowRight, Shield } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { useToast } from "../hooks/useToast";

export default function Newsletter() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      addToast({
        type: "error",
        title: t("newsletter.error_title", "Email required"),
        description: t("newsletter.error_desc", "Please enter your email address."),
      });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setEmail("");
      addToast({
        type: "success",
        title: t("newsletter.success_title", "You're subscribed!"),
        description: t("newsletter.success_desc", "Check your inbox for a confirmation email."),
      });
    }, 800);
  };

  const benefits = [
    {
      icon: Sparkles,
      titleKey: "newsletter.benefit_1_title",
      titleDefault: "Weekly AI Agent Tips",
      descKey: "newsletter.benefit_1_desc",
      descDefault: "Actionable strategies you can implement immediately — no fluff, just practical advice.",
    },
    {
      icon: BookOpen,
      titleKey: "newsletter.benefit_2_title",
      titleDefault: "Exclusive Guides",
      descKey: "newsletter.benefit_2_desc",
      descDefault: "Deep-dive guides and frameworks available only to subscribers.",
    },
    {
      icon: Zap,
      titleKey: "newsletter.benefit_3_title",
      titleDefault: "Early Access",
      descKey: "newsletter.benefit_3_desc",
      descDefault: "Be the first to know about new features, integrations, and product updates.",
    },
    {
      icon: Users,
      titleKey: "newsletter.benefit_4_title",
      titleDefault: "Community Insights",
      descKey: "newsletter.benefit_4_desc",
      descDefault: "Learn from real deployment stories and benchmarks shared by other business leaders.",
    },
  ];

  return (
    <>
      <SEOHead
        title={t("newsletter.seo_title", "Newsletter — Weekly AI Agent Insights | Max Employee")}
        description={t("newsletter.seo_desc", "Join 2,000+ business leaders getting actionable AI agent tips every week. Free newsletter from the Max Employee team.")}
        url="/newsletter"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
            { "@type": "ListItem", position: 2, name: "Newsletter", item: "https://maxemployee.com/newsletter" }
            ]
          }
        ]}
      />
      <div className="min-h-screen bg-[#09090B] text-white">
        <Header />

        <main id="main-content">
          {/* Hero Section */}
          <section className="relative overflow-hidden">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-16 sm:pt-24 pb-12 sm:pb-20">
              <div className="max-w-3xl mx-auto text-center">
                {/* Label */}
                <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 px-4 py-1.5 mb-6 sm:mb-8">
                  <Mail className="w-3.5 h-3.5 text-amber-500" aria-hidden="true" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
                    {t("newsletter.label", "Newsletter")}
                  </span>
                </div>

                <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] mb-4 sm:mb-6">
                  {t("newsletter.headline", "Get Weekly AI Agent Insights")}
                </h1>

                <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed">
                  {t("newsletter.subtitle", "Join 2,000+ business leaders getting actionable AI agent tips every week. No spam, unsubscribe anytime.")}
                </p>

                {/* Email Form */}
                {submitted ? (
                  <div className="max-w-md mx-auto border border-emerald-500/30 bg-emerald-500/5 p-6 flex items-center gap-3" role="alert">
                    <CheckCircle className="w-6 h-6 text-emerald-500 shrink-0" aria-hidden="true" />
                    <div className="text-left">
                      <p className="text-emerald-400 font-display text-sm uppercase">
                        {t("newsletter.success_title", "You're subscribed!")}
                      </p>
                      <p className="text-emerald-500/70 text-sm mt-1">
                        {t("newsletter.success_desc", "Check your inbox for a confirmation email.")}
                      </p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="max-w-md mx-auto" noValidate>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <label htmlFor="newsletter-email" className="sr-only">
                        {t("newsletter.email_label", "Email address")}
                      </label>
                      <input
                        id="newsletter-email"
                        type="email"
                        required
                        placeholder={t("newsletter.email_placeholder", "Enter your email")}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="flex-1 px-4 py-3 bg-zinc-950/60 border border-zinc-800 text-white placeholder:text-zinc-600 font-mono text-sm focus:outline-none focus:border-amber-500/50 min-h-[48px]"
                        aria-label={t("newsletter.email_label", "Email address")}
                      />
                      <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black px-6 py-3 font-bold uppercase tracking-wider text-xs transition-colors min-h-[48px]"
                      >
                        {loading ? (
                          t("newsletter.subscribing", "Subscribing...")
                        ) : (
                          <>
                            {t("newsletter.cta", "Subscribe")}
                            <ArrowRight className="w-4 h-4" aria-hidden="true" />
                          </>
                        )}
                      </button>
                    </div>
                    <p className="flex items-center gap-1.5 justify-center text-zinc-600 text-xs mt-3 font-mono">
                      <Shield className="w-3 h-3" aria-hidden="true" />
                      {t("newsletter.privacy", "No spam, unsubscribe anytime. We respect your privacy.")}
                    </p>
                  </form>
                )}

                {/* Social proof */}
                <div className="flex items-center justify-center gap-6 mt-8 sm:mt-10">
                  <div className="flex -space-x-2" aria-hidden="true">
                    {[
                      "bg-amber-500",
                      "bg-blue-500",
                      "bg-emerald-500",
                      "bg-purple-500",
                      "bg-rose-500",
                    ].map((color, i) => (
                      <div
                        key={i}
                        className={`w-8 h-8 rounded-full ${color} border-2 border-[#09090B] flex items-center justify-center text-[10px] font-bold text-white`}
                      >
                        {String.fromCharCode(65 + i)}
                      </div>
                    ))}
                  </div>
                  <span className="text-zinc-500 text-sm font-mono">
                    {t("social_proof", "Join 2,000+ subscribers")}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-20">
            <div className="text-center mb-10 sm:mb-14">
              <h2 className="font-display text-2xl sm:text-4xl uppercase mb-3">
                {t("newsletter.benefits_title", "What You'll Get")}
              </h2>
              <p className="text-zinc-400 max-w-xl mx-auto">
                {t("newsletter.benefits_subtitle", "Every week, we send you the most important AI agent insights — curated, practical, and ready to implement.")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {benefits.map((benefit, i) => (
                <div
                  key={i}
                  className="border border-zinc-800 bg-zinc-950/40 p-5 sm:p-6 hover:border-zinc-700 transition-colors group"
                >
                  <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4 group-hover:bg-amber-500/20 transition-colors">
                    <benefit.icon className="w-5 h-5 text-amber-500" aria-hidden="true" />
                  </div>
                  <h3 className="font-display text-sm uppercase text-white mb-2">
                    {t(benefit.titleKey, benefit.titleDefault)}
                  </h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    {t(benefit.descKey, benefit.descDefault)}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* What to Expect Section */}
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-12 sm:pb-20">
            <div className="border border-zinc-800 bg-zinc-950/40 p-6 sm:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl uppercase mb-4">
                    {t("newsletter.expect_title", "What to Expect")}
                  </h2>
                  <ul className="space-y-3">
                    {[
                      t("newsletter.expect_1", "One email per week — no daily spam"),
                      t("newsletter.expect_2", "Real case studies from AI agent deployments"),
                      t("newsletter.expect_3", "Benchmarks and metrics you can compare against"),
                      t("newsletter.expect_4", "Tips from our team of AI researchers"),
                      t("newsletter.expect_5", "Early access to new features and guides"),
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-zinc-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-zinc-900/60 border border-zinc-800 p-5 sm:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-amber-500 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-black" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-display text-xs uppercase text-white">
                        {t("newsletter.sample_label", "Sample Issue")}
                      </p>
                      <p className="text-zinc-500 text-xs font-mono">
                        {t("newsletter.sample_date", "June 2026")}
                      </p>
                    </div>
                  </div>
                  <p className="text-zinc-300 text-sm font-mono mb-3">
                    {t("newsletter.sample_subject", "Subject: How we saved 40 hours/week with a single AI agent")}
                  </p>
                  <p className="text-zinc-500 text-sm leading-relaxed">
                    {t("newsletter.sample_preview", "This week: The exact workflow we used to automate customer support for a 50-person e-commerce company — including the prompts, integrations, and metrics that made it work...")}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-16 sm:pb-24">
            <div className="text-center">
              <h2 className="font-display text-2xl sm:text-4xl uppercase mb-4">
                {t("newsletter.final_cta_title", "Ready to Level Up?")}
              </h2>
              <p className="text-zinc-400 max-w-lg mx-auto mb-8">
                {t("newsletter.final_cta_desc", "Join thousands of business leaders who start their week with actionable AI insights.")}
              </p>
              <button
                onClick={() => document.getElementById("newsletter-email")?.focus()}
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-8 py-4 font-bold uppercase tracking-wider text-sm transition-colors"
              >
                {t("newsletter.cta", "Subscribe")}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
